import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import api from "../lib/api";
import SafeImage from "./SafeImage";

const CATEGORY_LABEL = {
  fr: { finance: "Finance", politique: "Politique", "socio-culturel": "Socio-culturel", interview: "Interview", top: "Classement", sport: "Sport" },
  en: { finance: "Finance", politique: "Politics", "socio-culturel": "Society", interview: "Interview", top: "Ranking", sport: "Sport" },
};

export default function FeaturedSlider({ lang = "fr" }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    api.get("/articles?limit=12")
      .then((r) => {
        const all = r.data || [];
        const withCover = all.filter((a) => a.cover_url && !a.is_premium).slice(0, 6);
        const fallback = all.filter((a) => a.cover_url).slice(0, 6);
        setItems(withCover.length >= 3 ? withCover : fallback);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || items.length < 2) return;
    intervalRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, 6000);
    return () => clearInterval(intervalRef.current);
  }, [items.length, paused]);

  if (items.length === 0) return null;
  const current = items[idx];
  const cat = CATEGORY_LABEL[lang]?.[current.category] || current.category;

  return (
    <section
      className="relative overflow-hidden mt-4"
      style={{ background: "var(--gai-grey-50)" }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      data-testid="featured-slider"
    >
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-20 md:py-24">
        <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
          <div>
            <div className="text-[0.66rem] font-bold tracking-[0.24em] uppercase mb-2" style={{ color: "var(--gai-red)" }}>
              {lang === "fr" ? "À la une" : "Featured"}
            </div>
            <h2 className="serif text-3xl md:text-4xl" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }}>
              {lang === "fr" ? "L'actualité GAI" : "GAI News"}
            </h2>
            <div className="h-[2px] w-16 mt-3" style={{ background: "var(--gai-red)" }} />
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
              className="p-3 border transition"
              style={{ borderColor: "var(--gai-grey-300)", color: "var(--gai-black)" }}
              aria-label="Précédent"
              data-testid="slider-prev"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % items.length)}
              className="p-3 border transition"
              style={{ borderColor: "var(--gai-grey-300)", color: "var(--gai-black)" }}
              aria-label="Suivant"
              data-testid="slider-next"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center min-h-[420px]">
          <Link
            to={`/articles/${current.slug}`}
            className="lg:col-span-7 block relative group gai-fade-up"
            key={`img-${current.id}-${idx}`}
            data-testid="slider-active-image"
          >
            <SafeImage
              src={current.cover_url}
              alt={current.title?.[lang] || current.title?.fr || ""}
              fallbackLabel={cat}
              loading="eager"
              className="w-full aspect-[16/10] object-cover"
            />
            <div className="absolute top-4 left-4 px-3 py-1.5 text-[0.6rem] font-bold tracking-[0.22em] uppercase" style={{ background: "var(--gai-red)", color: "var(--gai-white)" }}>
              {cat}
            </div>
          </Link>

          <div className="lg:col-span-5 gai-fade-up" key={`txt-${current.id}-${idx}`}>
            <div className="text-[0.66rem] font-bold tracking-[0.22em] uppercase mb-3" style={{ color: "var(--gai-red)" }}>
              {String(idx + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </div>
            <h3 className="serif text-3xl md:text-4xl leading-tight" style={{ color: "var(--gai-black)", fontFamily: "'Playfair Display', serif" }} data-testid="slider-active-title">
              {current.title?.[lang] || current.title?.fr}
            </h3>
            <p className="mt-5 text-base md:text-lg leading-relaxed" style={{ color: "var(--gai-grey-700)" }}>
              {(current.excerpt?.[lang] || current.excerpt?.fr || "").slice(0, 240)}
              {(current.excerpt?.[lang] || current.excerpt?.fr || "").length > 240 ? "…" : ""}
            </p>
            <div className="mt-7">
              <Link to={`/articles/${current.slug}`} className="gai-btn-gold" data-testid="slider-cta">
                {lang === "fr" ? "Lire l'article" : "Read the article"} <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-10 flex gap-1.5 justify-center" data-testid="slider-indicators">
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Aller à l'article ${i + 1}`}
              className="h-[2px] transition-all"
              style={{
                width: i === idx ? "48px" : "16px",
                background: i === idx ? "var(--gai-red)" : "var(--gai-grey-300)",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
